import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useToast } from '@chakra-ui/react'
import { api } from '../../../../services/api'
import { useInfoCustomer } from '../../../../hooks/useInfoCustomer'
import { useUser } from '../../../../hooks/useUser'

export function useSolicitarTroca() {
  const { id: paramNumNota, sequency } = useParams()
  const [numNota, setNumNota] = useState(paramNumNota || '')
  const [selectedProduto, setSelectedProduto] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [photos, setPhotos] = useState([null, null, null])
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [showCamera, setShowCamera] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [observacao, setObservacao] = useState('')
  const [quantidade, setQuantidade] = useState(1)

  const { codigo_usuario } = useUser()

  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const toast = useToast()
  const navigate = useNavigate()
  const { infoCustomer } = useInfoCustomer()

  useEffect(() => {
    if (paramNumNota) {
      setNumNota(paramNumNota)
      setTimeout(() => {
        refetch()
      }, 300)
    }
  }, [paramNumNota])

  const {
    data: pedidoData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ['pedido-solicitar-troca', numNota],
    async () => {
      if (!numNota) return null
      const response = await api.get(`/ordem-producao/listar-status/${numNota}`)
      return response.data
    },
    {
      enabled: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutos
      onSuccess: () => {
        setIsSearching(false)
      },
      onError: () => {
        setIsSearching(false)
        toast({
          title: 'Erro na busca',
          description: error?.response?.data?.message || 'Não foi possível buscar os dados do pedido',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    }
  )

  const solicitarTrocaMutation = useMutation(
    async (formData) => {
      // Atualizado para o novo endpoint v2
      return await api.post('/ordem-producao/solicitar-troca-v2', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    {
      onSuccess: () => {
        toast({
          title: 'Troca solicitada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })

        navigate(`/status-pedido/${numNota}/${sequency}`)
      },
      onError: (error) => {
        toast({
          title: 'Erro ao solicitar troca',
          description: error?.response?.data?.message || 'Ocorreu um erro ao processar a solicitação de troca',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
        setSubmitting(false)
      },
    }
  )

  const handleSearch = async () => {
    if (!numNota) return
    setIsSearching(true)
    refetch()
  }

  const handleProdutoChange = (e) => {
    setSelectedProduto(e.target.value)
  }

  const openCamera = async (index) => {
    try {
      setCurrentPhotoIndex(index)
      setShowCamera(true)

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      }

      setTimeout(async () => {
        if (videoRef.current) {
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          videoRef.current.srcObject = stream
        }
      }, 100)
    } catch (error) {
      console.error('Error opening the camera:', error)
      toast({
        title: 'Erro de câmera',
        description: 'Não foi possível acessar a câmera. Verifique as permissões.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      setShowCamera(false)
    }
  }

  const capturePhoto = () => {
    const canvas = canvasRef.current
    const video = videoRef.current

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' })

        const newPhotos = [...photos]
        newPhotos[currentPhotoIndex] = {
          file,
          preview: URL.createObjectURL(blob),
        }

        setPhotos(newPhotos)

        closeCamera()
      },
      'image/jpeg',
      0.8
    )
  }

  const closeCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject
      const tracks = stream.getTracks()
      tracks.forEach((track) => track.stop())
    }
    setShowCamera(false)
  }

  const removePhoto = (index) => {
    const newPhotos = [...photos]
    newPhotos[index] = null
    setPhotos(newPhotos)
  }

  const allPhotosTaken = () => {
    return photos.every((photo) => photo !== null)
  }

  const atLeastOnePhotoTaken = () => {
    return photos.some((photo) => photo !== null)
  }

  const handleSubmit = () => {
    if (!selectedProduto) {
      toast({
        title: 'Selecione um produto',
        description: 'É necessário selecionar um produto para solicitar a troca',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!atLeastOnePhotoTaken()) {
      toast({
        title: 'Adicione fotos',
        description: 'É necessário adicionar pelo menos uma foto do produto',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!observacao.trim()) {
      toast({
        title: 'Adicione uma observação',
        description: 'É necessário adicionar uma observação sobre o motivo da troca',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!quantidade || quantidade <= 0) {
      toast({
        title: 'Quantidade inválida',
        description: 'É necessário informar uma quantidade válida',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const selectedItem = pedidoData.itens.find((item) => item.sequencia.toString() === selectedProduto)
    if (!selectedItem) {
      toast({
        title: 'Erro',
        description: 'Produto selecionado não encontrado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setSubmitting(true)

    const formData = new FormData()

    photos.forEach((photo, index) => {
      if (photo) {
        formData.append(`file[${index}]`, photo.file)
      }
    })

    formData.append('codigo_produto', selectedItem.codigo_produto)
    formData.append('descricao', observacao)
    formData.append('numero_nota', pedidoData.numero_nota)
    formData.append('numero_unico', pedidoData.numero_unico || 0)
    formData.append('quantidade', quantidade)
    formData.append('sequencia', selectedItem.sequencia)
    formData.append('cod_usu', codigo_usuario || '')

    solicitarTrocaMutation.mutate(formData)
  }

  return {
    numNota,
    setNumNota,
    selectedProduto,
    setSelectedProduto,
    pedidoData,
    isLoading,
    isError,
    error,
    isFetching,
    isSearching,
    photos,
    showCamera,
    currentPhotoIndex,
    videoRef,
    canvasRef,
    submitting: submitting || solicitarTrocaMutation.isLoading,
    observacao,
    setObservacao,
    quantidade,
    setQuantidade,
    handleSearch,
    handleProdutoChange,
    openCamera,
    capturePhoto,
    closeCamera,
    removePhoto,
    handleSubmit,
    allPhotosTaken,
    atLeastOnePhotoTaken,
  }
}
